import {
    Stack,
    TextField,
    PrimaryButton,
    Dropdown,
    IDropdownOption,
    Spinner,
    SpinnerSize,
    Text,
    MessageBar,
    MessageBarType,
} from '@fluentui/react';
import React, { useCallback, useContext, useState } from 'react';
import { usePromise } from '../../../../Common/hooks/usePromise';
import { reqeueDeadLetterMessages } from '../../../../AGCP/api/AGCPApi';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';

interface IRequeueDeadLetterMessagesProps {}

export const RequeueDeadLetterMessages: React.FC<IRequeueDeadLetterMessagesProps> =
    (props: IRequeueDeadLetterMessagesProps) => {
        const themeContext = useContext(ThemeContext);
        const theme = getThemeFromString(themeContext.themeName);
        const [queueName, setQueueName] = useState<string>('');
        const [messageCount, setMessageCount] = useState<string>('10');
        const [region, setRegion] = useState<
            'primary' | 'secondary' | undefined
        >(undefined);
        const [isLoading, setIsLoading] = useState<boolean>(false);

        const stackTokens = { childrenGap: 16 };

        const regionOptions: IDropdownOption[] = [
            { key: 'primary', text: 'Primary' },
            { key: 'secondary', text: 'Secondary' },
        ];

        const handleQueueNameChange = (
            event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string | undefined
        ) => {
            setQueueName(newValue ?? '');
        };

        const handleMessageCountChange = (
            event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string | undefined
        ) => {
            if (!newValue) {
                newValue = '';
            }

            const re = /^\d+$/;
            if (re.test(newValue)) {
                setMessageCount(newValue);
            } else {
                setMessageCount('');
            }
        };

        const handleRegionChange = (
            event: React.FormEvent<HTMLDivElement>,
            option?: IDropdownOption
        ) => {
            if (option) {
                setRegion(option.key as 'primary' | 'secondary');
            }
        };

        const requeueCallback = useCallback(async () => {
            if (queueName && messageCount && region) {
                try {
                    const result = await reqeueDeadLetterMessages(
                        queueName,
                        parseInt(messageCount),
                        region
                    );
                    return result;
                } finally {
                    // Always reset loading so the button re-enables even if
                    // the request rejects.
                    setIsLoading(false);
                }
            }
        }, [queueName, messageCount, region]);

        const [result, error, isLoaded, start, cancel] = usePromise(
            requeueCallback,
            false
        );

        const handleRequeueClick = () => {
            setIsLoading(true);
            start();
        };

        return (
            <Stack tokens={stackTokens}>
                <div
                    style={{
                        padding: '12px',
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                    }}
                >
                    <TextField
                        label="Queue Name"
                        placeholder="Enter the queue name"
                        value={queueName}
                        onChange={handleQueueNameChange}
                        borderless
                    />
                </div>
                <div
                    style={{
                        padding: '12px',
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                    }}
                >
                    <TextField
                        label="Message Count (max 1000)"
                        placeholder="Enter the message count"
                        maxLength={4}
                        value={messageCount}
                        onChange={handleMessageCountChange}
                        borderless
                    />
                </div>
                <div
                    style={{
                        padding: '12px',
                        backgroundColor: theme.palette.white,
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                    }}
                >
                    <Dropdown
                        label="Region"
                        placeholder="Select a region"
                        options={regionOptions}
                        selectedKey={region}
                        onChange={handleRegionChange}
                        styles={{
                            dropdown: { border: 'none' },
                        }}
                    />
                </div>

                <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: 12 }}
                >
                    <PrimaryButton
                        text={
                            isLoading
                                ? 'Requeuing...'
                                : 'Requeue Dead Letter Messages'
                        }
                        iconProps={{ iconName: 'Refresh' }}
                        onClick={handleRequeueClick}
                        disabled={
                            isLoading || !queueName || !messageCount || !region
                        }
                        styles={{
                            root: {
                                backgroundColor: theme.palette.themePrimary,
                                borderColor: theme.palette.themePrimary,
                            },
                            rootHovered: {
                                backgroundColor: theme.palette.themeDark,
                                borderColor: theme.palette.themeDark,
                            },
                        }}
                    />
                    {isLoading && (
                        <Spinner
                            size={SpinnerSize.medium}
                            label="Requeuing..."
                        />
                    )}
                </Stack>

                {/* Requeue error */}
                {error && !isLoading && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        Error: {error.message ?? 'The requeue request failed.'}
                    </MessageBar>
                )}

                {/* Requeue result */}
                {result && !isLoading && (
                    <Stack tokens={{ childrenGap: 16 }}>
                        {/* Summary Card */}
                        <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor:
                                        theme.palette.neutralLighter,
                                    borderRadius: '4px',
                                    minWidth: '150px',
                                    flex: '1 1 auto',
                                }}
                            >
                                <Text
                                    variant="small"
                                    styles={{
                                        root: {
                                            color: theme.palette
                                                .neutralSecondary,
                                            fontWeight: 600,
                                        },
                                    }}
                                >
                                    Messages Requeued
                                </Text>
                                <Text
                                    variant="xLarge"
                                    styles={{
                                        root: {
                                            color: theme.palette.themePrimary,
                                            fontWeight: 600,
                                            display: 'block',
                                        },
                                    }}
                                >
                                    {result.data ?? 0}
                                </Text>
                            </div>
                        </Stack>

                        {/* Status message */}
                        <MessageBar
                            messageBarType={
                                result.succeeded
                                    ? MessageBarType.success
                                    : MessageBarType.error
                            }
                        >
                            {result.message ??
                                (result.succeeded
                                    ? 'Dead letter messages requeued successfully.'
                                    : 'Failed to requeue dead letter messages.')}
                        </MessageBar>
                    </Stack>
                )}
            </Stack>
        );
    };
